import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { 
  ClockIcon, 
  PlayIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import api from '../services/api';

const AdminCronJobs = () => {
  const queryClient = useQueryClient();
  const [executing, setExecuting] = useState(null);

  // Récupérer le statut des tâches
  const { data: cronStatus, isLoading } = useQuery(
    'cronStatus',
    async () => {
      const response = await api.get('/cron/status');
      return response.data;
    },
    {
      refetchInterval: 60000 // Rafraîchir chaque minute
    }
  );

  // Mutation pour exécuter une tâche
  const executeMutation = useMutation(
    async (taskName) => {
      const response = await api.post(`/cron/run/${taskName}`);
      return response.data;
    },
    {
      onSuccess: (data, taskName) => {
        toast.success(`Tâche "${taskName}" exécutée avec succès`);
        setExecuting(null);
        queryClient.invalidateQueries('cronStatus');
      },
      onError: (error, taskName) => {
        toast.error(`Erreur lors de l'exécution de "${taskName}": ${error.response?.data?.message || error.message}`);
        setExecuting(null);
      }
    }
  );

  const handleExecute = (taskName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir exécuter la tâche "${taskName}" maintenant ?`)) {
      setExecuting(taskName);
      executeMutation.mutate(taskName);
    }
  };

  const getTaskIcon = (taskName) => {
    const icons = {
      savings: '💰',
      renewals: '🔄',
      reminders: '🔔',
      overdue: '⚠️',
      all: '🚀'
    };
    return icons[taskName] || '📋';
  };

  const getTaskColor = (taskName) => {
    const colors = {
      savings: 'bg-green-100 text-green-800 border-green-200',
      renewals: 'bg-blue-100 text-blue-800 border-blue-200',
      reminders: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      overdue: 'bg-red-100 text-red-800 border-red-200',
      all: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[taskName] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const tasks = cronStatus?.scheduledTasks || [];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg shadow-lg p-6 mb-8">
        <div className="flex items-center space-x-4">
          <ClockIcon className="h-12 w-12 text-white" />
          <div>
            <h1 className="text-3xl font-bold text-white">Tâches Planifiées</h1>
            <p className="text-indigo-100 mt-1">
              Gestion des tâches automatiques du système
            </p>
          </div>
        </div>
        
        {/* Informations serveur */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <p className="text-indigo-100 text-sm">Heure serveur</p>
            <p className="text-white font-bold text-lg">
              {cronStatus?.serverTime ? new Date(cronStatus.serverTime).toLocaleString('fr-FR') : '-'}
            </p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4">
            <p className="text-indigo-100 text-sm">Fuseau horaire</p>
            <p className="text-white font-bold text-lg">{cronStatus?.timezone || '-'}</p>
          </div>
        </div>
      </div>

      {/* Tâches planifiées */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <ArrowPathIcon className="h-6 w-6 mr-2 text-indigo-600" />
          Tâches Automatiques
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tasks.map((task) => (
            <div
              key={task.name}
              className={`border-2 rounded-lg p-6 ${getTaskColor(task.name)} hover:shadow-lg transition-shadow`}
            >
              {/* En-tête de la tâche */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{getTaskIcon(task.name)}</span>
                  <div>
                    <h3 className="font-bold text-lg">{task.description}</h3>
                    <p className="text-sm opacity-75 mt-1">
                      <ClockIcon className="h-4 w-4 inline mr-1" />
                      {task.schedule}
                    </p>
                  </div>
                </div>
              </div>

              {/* Expression cron */}
              <div className="bg-white bg-opacity-50 rounded px-3 py-2 mb-4">
                <p className="text-xs font-mono">{task.cronExpression}</p>
              </div>

              {/* Bouton d'exécution */}
              <button
                onClick={() => handleExecute(task.name)}
                disabled={executing === task.name}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all
                  ${executing === task.name
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-white hover:bg-opacity-90 shadow-md hover:shadow-lg'
                  }`}
              >
                {executing === task.name ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Exécution...</span>
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-5 w-5" />
                    <span>Exécuter maintenant</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Exécution groupée */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg shadow-md p-6 border-2 border-purple-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Exécuter Toutes les Tâches
            </h3>
            <p className="text-gray-600">
              Lance toutes les tâches planifiées en séquence. Utilisez avec précaution.
            </p>
          </div>
          <button
            onClick={() => handleExecute('all')}
            disabled={executing === 'all'}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-bold transition-all
              ${executing === 'all'
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg'
              }`}
          >
            {executing === 'all' ? (
              <>
                <LoadingSpinner size="small" />
                <span>Exécution...</span>
              </>
            ) : (
              <>
                <PlayIcon className="h-5 w-5" />
                <span>Tout Exécuter</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Informations */}
      <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-6 w-6 text-blue-500 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-blue-900 mb-2">Informations Importantes</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Génération SAVINGS</strong> : Crée une cotisation mensuelle pour chaque police d'épargne active
                </span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Reconduction HEALTH</strong> : Reconduit automatiquement les polices santé arrivées à terme (si pas de résiliation)
                </span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Rappels</strong> : Envoie des notifications 7 jours avant l'échéance des cotisations
                </span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Marquage retards</strong> : Marque automatiquement les cotisations en retard
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCronJobs;
